import { useMemo, useState } from 'react';
import { CalendarCheck, MapPin, Users, Swords, Clock, Package } from 'lucide-react';
import { courtDisplayName } from '../../../engine';
import { useT } from '../../../i18n';
import {
  memberBookings,
  memberActivities,
  memberEquipmentBookings,
  attachedEquipment,
  assetNameFor,
  splitUpcomingPast,
  relativeDayLabel,
  activityWhenLabel,
  planOpenSlots,
  type MemberActivity,
  type MemberBooking,
  type MemberEquipmentBooking,
} from '../memberData';
import { Card, EmptyState, Segments, TeamNames } from '../mobileUi';
import { PayForBooking, paymentDueFor } from '../../booking/PayForBooking';
import { AcrossClubsReservations } from '../AcrossClubs';
import { CancelOwnBooking } from '../CancelOwnBooking';

export interface MobileReservationsProps {
  member: any;
  bookings: any[];
  plannedMatches: any[];
  activities: any[];
  /**
   * EQUIPMENT (docs/domains/ASSET_BOOKING.md, slice 2): the venue's gear, for
   * names, and every reservation of it — the member's own marked `mine`. A
   * machine booked on its own is listed as its own row; one riding with a
   * court booking is named on that booking's row.
   */
  assets?: any[];
  assetBookings?: any[];
  effectivePlayerDB: any[];
  courtMeta?: any;
  /**
   * Offer «Alle klubber» — the account's own bookings and sign-ups in every
   * club (#760). Only for an account on the player surface: a legacy member
   * session and the club portal's preview have no account to ask about.
   */
  acrossClubs?: boolean;
}

type Seg = 'upcoming' | 'past' | 'all';
type Item =
  | { kind: 'booking'; key: string; startMs: number; endMs: number; b: MemberBooking }
  | { kind: 'activity'; key: string; startMs: number; endMs: number; a: MemberActivity }
  | { kind: 'equipment'; key: string; startMs: number; endMs: number; e: MemberEquipmentBooking };

// «Reservasjoner» — everything the member has on the calendar in one list:
// court bookings, single matches they are in (their own or joined), activity
// sign-ups and equipment booked on its own. Same derivations as Home, just
// complete.
export function MobileReservations({
  member,
  bookings,
  plannedMatches,
  activities,
  assets = [],
  assetBookings = [],
  effectivePlayerDB,
  courtMeta,
  acrossClubs = false,
}: MobileReservationsProps) {
  const t = useT();
  const [seg, setSeg] = useState<Seg>('upcoming');
  const db = useMemo(() => effectivePlayerDB || [], [effectivePlayerDB]);

  const items = useMemo<Item[]>(() => {
    const mb = memberBookings({ bookings, plannedMatches, activities, member });
    const ma = memberActivities(activities, member?.id, bookings);
    const me = member ? memberEquipmentBookings(assetBookings) : [];
    // A booking already linked to an activity is listed once, as the booking.
    const viaBooking = new Set(mb.filter((b) => b.activity).map((b) => b.activity.id));
    return [
      ...mb.map((b) => ({
        kind: 'booking' as const,
        key: `b:${b.booking.id}`,
        startMs: b.startMs,
        endMs: b.endMs,
        b,
      })),
      ...ma
        .filter((a) => !viaBooking.has(a.activity.id))
        .map((a) => ({
          kind: 'activity' as const,
          key: `a:${a.activity.id}`,
          startMs: a.startMs,
          endMs: a.endMs,
          a,
        })),
      ...me.map((e) => ({
        kind: 'equipment' as const,
        key: `e:${e.assetBooking.id}`,
        startMs: e.startMs,
        endMs: e.endMs,
        e,
      })),
    ];
  }, [bookings, plannedMatches, activities, assetBookings, member]);

  const { upcoming, past } = splitUpcomingPast(items);
  const list = seg === 'upcoming' ? upcoming : past;

  return (
    <div className="px-4 pb-4 space-y-3">
      <h1
        className="font-display font-semibold text-ink leading-tight pt-2"
        style={{ fontSize: 26 }}
      >
        {t('mobile.reservations.title')}
      </h1>
      <Segments<Seg>
        value={seg}
        onChange={setSeg}
        options={[
          { id: 'upcoming', label: t('mobile.reservations.upcoming'), count: upcoming.length },
          { id: 'past', label: t('mobile.reservations.past'), count: past.length },
          // THE CLUB'S TWO SEGMENTS ARE UNCHANGED; this one sits beside them.
          ...(acrossClubs ? [{ id: 'all' as const, label: t('mobile.history.allClubs') }] : []),
        ]}
      />
      {seg === 'all' ? (
        <AcrossClubsReservations />
      ) : list.length === 0 ? (
        <EmptyState
          icon={<CalendarCheck size={22} />}
          title={
            seg === 'upcoming'
              ? t('mobile.reservations.emptyUpcoming')
              : t('mobile.reservations.emptyPast')
          }
          hint={
            seg === 'upcoming'
              ? t('mobile.reservations.emptyUpcomingHint')
              : t('mobile.reservations.emptyPastHint')
          }
        />
      ) : (
        // Two-up on a wide screen, in the same order.
        <div className="space-y-2 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-2 @wide:items-start">
          {list.map((it) =>
            it.kind === 'booking' ? (
              <BookingRow
                key={it.key}
                mb={it.b}
                db={db}
                meId={member?.id}
                courtMeta={courtMeta}
                equipment={attachedEquipment(assetBookings, it.b.booking.id).map((ab: any) =>
                  assetNameFor(assets, ab.assetId),
                )}
              />
            ) : it.kind === 'equipment' ? (
              <EquipmentRow
                key={it.key}
                e={it.e}
                name={assetNameFor(assets, it.e.assetBooking.assetId)}
              />
            ) : (
              <ActivityRow key={it.key} ma={it.a} bookings={bookings} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A MACHINE BOOKED ON ITS OWN (slice 2) — the same shape as a court booking's
 * row, with the way to pay it: its own hold, its own payment path, and the
 * same «betalt» caption once the server says so.
 */
function EquipmentRow({ e, name }: { e: MemberEquipmentBooking; name: string }) {
  const t = useT();
  const ab = e.assetBooking;
  const [paidHere, setPaidHere] = useState(false);
  return (
    <Card accent="#c2f02f">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
        {relativeDayLabel(ab.date)} · {ab.startTime}–{ab.endTime}
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <div className="text-[14px] font-semibold text-ink truncate flex items-center gap-1.5">
          <Package size={14} className="text-ink-3" />
          {t('mobile.reservations.equipmentBooking')}
        </div>
        <span className="font-mono text-[10px] text-ink-2 flex items-center gap-1 flex-shrink-0">
          {name}
        </span>
      </div>
      {paymentDueFor(ab) && !paidHere && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[12px] text-ink-2">{t('mobile.reservations.unpaid')}</span>
          <PayForBooking
            booking={ab}
            subject="equipment"
            summary={`${name} · ${relativeDayLabel(ab.date)} ${ab.startTime}–${ab.endTime}`}
            onPaid={() => setPaidHere(true)}
          />
        </div>
      )}
      {paidHere && (
        <div className="mt-2 text-[12px] font-semibold" data-testid="reservation-paid">
          {t('mobile.reservations.paid')}
        </div>
      )}
    </Card>
  );
}

function BookingRow({
  mb,
  db,
  meId,
  courtMeta,
  equipment = [],
}: {
  mb: MemberBooking;
  db: any[];
  meId?: any;
  courtMeta: any;
  /** The names of the equipment riding with this booking (slice 2). */
  equipment?: string[];
}) {
  const t = useT();
  const b = mb.booking;
  const p = mb.plan;
  /**
   * THE SERVER SAID THIS ONE IS PAID, after the member paid it right here (#284).
   *
   * The row renders «Ikke betalt» from `paymentDueFor(b)`, and `b` is the booking
   * as it was loaded — it does not change when a payment lands. So without this,
   * `PayForBooking` would report «Betalt» while the caption beside it still said
   * «Ikke betalt»: the component had learned the webhook landed and the row
   * containing it had not.
   *
   * Local to this row and to this visit. It is the SERVER's word, arriving through
   * `onPaid` — which fires only after the poll has observed `paymentStatus: 'paid'`,
   * never on the card going through — and the next load reads the real value.
   */
  const [paidHere, setPaidHere] = useState(false);
  /**
   * CANCELLED HERE, on the server's word (#1135): the row says so until the next
   * load drops it — never before the command answered `cancelled`.
   */
  const [cancelledHere, setCancelledHere] = useState<number | null>(null);
  const title = p
    ? t('mobile.reservations.singleMatch')
    : mb.activity?.config?.name || t('mobile.reservations.courtBooking');
  const open = p ? planOpenSlots(p) : 0;
  return (
    <Card accent={p ? '#0ea5e9' : mb.activity ? '#84cc16' : '#c2f02f'}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
        {relativeDayLabel(b.date)} · {b.startTime}–{b.endTime}
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <div className="text-[14px] font-semibold text-ink truncate flex items-center gap-1.5">
          {p ? (
            <Swords size={14} className="text-ink-3" />
          ) : (
            <Clock size={14} className="text-ink-3" />
          )}
          {title}
        </div>
        <span className="font-mono text-[10px] text-ink-2 flex items-center gap-1 flex-shrink-0">
          <MapPin size={11} /> {courtDisplayName(b.court, courtMeta || {})}
        </span>
      </div>
      {/* THE EQUIPMENT RIDING WITH IT (slice 2), by name — paid with the court,
          moved with the court, released with the court. */}
      {equipment.length > 0 && (
        <div
          className="text-[12px] text-ink-2 mt-1 flex items-center gap-1"
          data-testid="booking-equipment"
        >
          <Package size={12} className="text-ink-3" />
          {t('mobile.reservations.withEquipment', { names: equipment.join(', ') })}
        </div>
      )}
      {/* ── AN UNPAID COURT BOOKING, AND THE WAY TO PAY IT (#284) ──────
          The durable entry point. Slice 5b offered payment only in the moment
          after booking, so one accidental close stranded a `pending` booking
          holding a court with no way back — and CLOCK 1's value is still open, so
          the hold is not guaranteed to lapse either. This is «the member can pay
          later», which until now was only a claim. */}
      {paymentDueFor(b) && !paidHere && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[12px] text-ink-2">{t('mobile.reservations.unpaid')}</span>
          <PayForBooking
            booking={b}
            summary={`${courtDisplayName(b.court, courtMeta || {})} · ${relativeDayLabel(b.date)} ${b.startTime}–${b.endTime}`}
            onPaid={() => setPaidHere(true)}
          />
        </div>
      )}
      {paidHere && (
        // ONE CAPTION, NOT TWO. The unpaid block goes entirely — label, button and
        // all — and this replaces it, so the row never shows «Betalt» and «Ikke
        // betalt» at once.
        <div className="mt-2 text-[12px] font-semibold" data-testid="reservation-paid">
          {t('mobile.reservations.paid')}
        </div>
      )}
      {/* «AVBESTILL» (#1135): only where the server sent `cancellable` — the
          member's own booking, strictly — and «for sent» once its deadline is past. */}
      {cancelledHere !== null ? (
        <div className="mt-2 text-[12px] font-semibold" data-testid="reservation-cancelled">
          {cancelledHere > 0
            ? t('mobile.reservations.cancel.doneRefund', {
                amount: String(cancelledHere / 100),
              })
            : t('mobile.reservations.cancel.done')}
        </div>
      ) : (
        b.cancellable &&
        mb.startMs > Date.now() && (
          <CancelOwnBooking
            id={b.id}
            cancellable={b.cancellable}
            summary={`${courtDisplayName(b.court, courtMeta || {})} · ${relativeDayLabel(b.date)} ${b.startTime}–${b.endTime}`}
            onCancelled={setCancelledHere}
          />
        )
      )}
      {p && (
        <div className="text-[12px] text-ink-2 mt-1 flex items-center justify-between gap-2">
          <span className="truncate">
            <TeamNames team={p.team1} db={db} meId={meId} /> <span className="text-ink-3">vs</span>{' '}
            <TeamNames team={p.team2} db={db} meId={meId} />
          </span>
          {open > 0 && (
            <span
              className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
              style={{ background: '#fef9c3', color: '#854d0e' }}
            >
              {open === 1
                ? t('mobile.reservations.openSlot', { count: open })
                : t('mobile.reservations.openSlots', { count: open })}
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

function ActivityRow({ ma, bookings }: { ma: MemberActivity; bookings: any[] }) {
  const t = useT();
  const a = ma.activity;
  return (
    <Card accent="#84cc16">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
        {activityWhenLabel(a, bookings)}
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <div className="text-[14px] font-semibold text-ink truncate flex items-center gap-1.5">
          <Users size={14} className="text-ink-3" /> {a.config?.name || t('mobile.common.activity')}
        </div>
        <span
          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
          style={{
            background: ma.registration?.status === 'waitlist' ? '#fef9c3' : '#f7fdde',
            color: '#0f1611',
          }}
        >
          {ma.registration?.status === 'waitlist'
            ? t('mobile.common.waitlist')
            : t('mobile.common.signedUp')}
        </span>
      </div>
    </Card>
  );
}
